import { motion } from 'framer-motion';
import { useLocation, useParams } from 'react-router-dom';
import {
  FaAward,
  FaBolt,
  FaBuilding,
  FaCertificate,
  FaChartLine,
  FaExchangeAlt,
  FaFileSignature,
  FaGlobeAsia,
  FaHandshake,
  FaIndustry,
  FaLandmark,
  FaLaptopCode,
  FaLightbulb,
  FaMoneyBillWave,
  FaPeopleArrows,
  FaPills,
  FaRocket,
  FaSeedling,
  FaUniversity,
} from 'react-icons/fa';

import { statesData, stateNames, defaultInitiatives, defaultPriorities } from './statesData';

const heroImage =
  'https://images.pexels.com/photos/17585996/pexels-photo-17585996.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=627&w=1200';

// ---- Design constants — same for every state, sirf content data-driven hai ----

const mandates = [
  {
    title: 'Policy Advocacy',
    text: 'Public policy, regulatory reforms and Ease of Doing Business inputs with Central and State Governments.',
    icon: FaLandmark,
  },
  {
    title: 'Industry Competitiveness',
    text: 'Focused support for manufacturing, MSMEs, infrastructure, green growth and business competitiveness.',
    icon: FaChartLine,
  },
  {
    title: 'Stakeholder Platform',
    text: 'Conferences, seminars, business forums and government-industry interactions across the state.',
    icon: FaPeopleArrows,
  },
];

const services = [
  { label: 'Investment facilitation', icon: FaBuilding },
  { label: 'Certificate of Origin service', icon: FaCertificate },
  { label: 'Overseas delegation support', icon: FaGlobeAsia },
  { label: 'Sector-specific MoU facilitation', icon: FaFileSignature },
  { label: 'Industry academia connect', icon: FaUniversity },
  { label: 'Local chamber collaboration', icon: FaHandshake },
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0 },
};

// Sector name se best-match icon nikalne ka helper, taaki naye sectors ke liye
// bhi manually icon map na karna pade.
const sectorIconRules = [
  [/agro|food|fisher/i, FaSeedling],
  [/renewable|energy|power/i, FaBolt],
  [/start-?up/i, FaRocket],
  [/intellectual|ipr/i, FaLightbulb],
  [/tax/i, FaMoneyBillWave],
  [/it\b|ites|e-commerce|digital|electronics|esdm/i, FaLaptopCode],
  [/msme|manufactur|mining|industr|engineering/i, FaIndustry],
  [/pharma|health|medical/i, FaPills],
  [/tourism|aviation|aerospace|hospitality/i, FaGlobeAsia],
  [/logistic|warehous|export/i, FaExchangeAlt],
];

function iconForSector(name) {
  const match = sectorIconRules.find(([regex]) => regex.test(name));
  return match ? match[1] : FaIndustry;
}

function toTitleCase(slug) {
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const defaultStateContent = {
  focusItems: ['Viksit Bharat 2047', 'Ease of Doing Business', 'Sustainable Development'],
  intro:
    'A focused ASSOCHAM platform strengthening the state\u2019s industrial ecosystem through policy advocacy, investment engagement, MSME growth, infrastructure dialogue, and sustainable development.',
  why:
    'This council works closely with the Government and industry stakeholders to advance policy clarity, sector action and long-term competitiveness for the state. Detailed content for this council is coming soon.',
  sectors: [],
  leadership: [],
  initiatives: [],
  priorities: [],
};

export default function StatePage() {
  // Route dono tarah se support: /:stateSlug (App.jsx me static path ke through)
  // ya agar kabhi /states/:stateSlug jaisa nested route use kiya toh useParams se bhi mil jayega.
  const { stateSlug } = useParams();
  const location = useLocation();
  const slug = stateSlug || location.pathname.replace(/^\//, '');

  const data = statesData[slug] || defaultStateContent;
  const name = data.name || `${stateNames[slug] || toTitleCase(slug)} State Development Council`;
  const initiatives = data.initiatives && data.initiatives.length > 0 ? data.initiatives : defaultInitiatives;
  const priorities = data.priorities && data.priorities.length > 0 ? data.priorities : defaultPriorities;

  return (
    <main className="min-h-screen overflow-hidden bg-[#FAF7F2] text-[#241A16] selection:bg-[#DA2128] selection:text-white">

     <section id="top" className="relative isolate min-h-[400px] overflow-hidden bg-[#FEF7F4] text-[#12100E]">
  <motion.img
    src={heroImage}
    alt={`${name} banner`}
    className="absolute inset-0 h-full w-full object-cover opacity-20 mix-blend-multiply"
    initial={{ scale: 1.05 }}
    animate={{ scale: 1 }}
    transition={{ duration: 1.4, ease: 'easeOut' }}
  />

  <div className="absolute inset-0 bg-[linear-gradient(110deg,#FEF7F4_0%,rgba(254,247,244,0.92)_45%,rgba(218,33,40,0.15)_100%)]" />

  <motion.div
    aria-hidden="true"
    className="absolute -right-32 -top-40 h-[26rem] w-[26rem] rounded-full border-[40px] border-[#F59C00]/15 pointer-events-none"
    animate={{ rotate: 360 }}
    transition={{ duration: 35, repeat: Infinity, ease: 'linear' }}
  />
  <motion.div
    aria-hidden="true"
    className="absolute -bottom-36 left-[8%] h-[20rem] w-[20rem] rounded-full border-[28px] border-[#DA2128]/15 pointer-events-none"
    animate={{ y: [0, -20, 0], x: [0, 10, 0] }}
    transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
  />

  <div className="relative mx-auto grid max-w-7xl items-end gap-12 px-6 py-12 lg:grid-cols-[1.15fr_0.85fr] lg:px-12 lg:py-16">
    <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.7 }}>
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.35em] text-[#D98200]">
        Industry Policy Forum
      </p>

      <h1
        className="max-w-3xl text-3xl font-medium leading-[1.15] tracking-tight text-[#12100E] md:text-5xl"
        style={{ fontFamily: 'Fraunces, Georgia, "Times New Roman", serif' }}
      >
        {name}
      </h1>

      <p className="mt-5 max-w-xl text-base leading-relaxed text-[#12100E]/80 font-normal">
        {data.intro || defaultStateContent.intro}
      </p>
    </motion.div>

    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.75, delay: 0.15 }}
      className="rounded-r-lg border-l-2 border-[#F59C00] bg-black/[0.03] p-6 backdrop-blur-md lg:p-8"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#12100E]/60">
        Council Focus
      </p>

      <div className="mt-4 space-y-3.5">
        {(data.focusItems || defaultStateContent.focusItems).map((item) => (
          <div
            key={item}
            className="flex items-center gap-3.5 text-base font-medium tracking-wide text-[#12100E]/95"
            style={{ fontFamily: 'Fraunces, Georgia, "Times New Roman", serif' }}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#F59C00]" />
            {item}
          </div>
        ))}
      </div>
    </motion.div>
  </div>
</section>

      <section id="mandate" className="mx-auto max-w-7xl px-6 py-16 lg:px-12 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr]">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[#DB6400]">
              Why the council exists
            </p>
            <h2
              className="mt-3 text-2xl font-medium leading-[1.25] tracking-tight text-[#241A16] md:text-4xl"
              style={{ fontFamily: 'Fraunces, Georgia, "Times New Roman", serif' }}
            >
              Government, industry and chambers on one operating table.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-[#241A16]/75 font-normal">
              {data.why || defaultStateContent.why}
            </p>
          </motion.div>

          <div className="relative border-l border-[#DA2128]/20 pl-8 font-sans">
            {mandates.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="relative pb-8 last:pb-0"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.12 }}
                >
                  <span className="absolute -left-[2.5rem] top-0.5 flex h-7 w-7 items-center justify-center rounded-sm bg-[#DA2128] text-white shadow-sm">
                    <Icon className="text-xs" />
                  </span>

                  <h3
                    className="text-lg font-medium tracking-tight text-[#DA2128]"
                    style={{ fontFamily: 'Fraunces, Georgia, "Times New Roman", serif' }}
                  >
                    {item.title}
                  </h3>

                  <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-[#241A16]/75 font-normal">
                    {item.text}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {data.leadership && data.leadership.length > 0 && (
        <section id="leadership" className="bg-[#241A16] text-white">
          <div className="mx-auto grid max-w-7xl gap-10 px-6 py-16 lg:grid-cols-[0.85fr_1.15fr] lg:px-12 lg:py-20">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[#F59C00]">
                Council Leadership
              </p>
              <h2
                className="mt-3 text-2xl font-medium leading-[1.25] tracking-tight text-white md:text-4xl"
                style={{ fontFamily: 'Fraunces, Georgia, "Times New Roman", serif' }}
              >
                Industry-led. Policy-focused.
              </h2>
              <p className="mt-4 text-base leading-relaxed text-white/75 font-normal">
                The council is guided by senior industry leaders and works with government departments, local
                chambers, sector associations and member companies.
              </p>
            </motion.div>

            <div className="divide-y divide-white/10 border-y border-white/10">
              {data.leadership.map((leader, index) => (
                <motion.div
                  key={leader.name}
                  className="grid gap-3 py-6 md:grid-cols-[140px_1fr] md:items-baseline"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.12 }}
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#F59C00]">
                    {leader.role}
                  </p>
                  <div>
                    <h3
                      className="text-xl font-medium tracking-tight text-white"
                      style={{ fontFamily: 'Fraunces, Georgia, "Times New Roman", serif' }}
                    >
                      {leader.name}
                    </h3>
                    <p className="mt-1 text-sm text-white/70 font-normal">
                      {leader.company} <span className="text-white/40">|</span> {leader.designation}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {data.sectors && data.sectors.length > 0 && (
        <section id="sectors" className="relative bg-[#FEF7F4] py-16 lg:py-20">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[#DB6400]">
                  Sectoral Engine
                </p>
                <h2
                  className="mt-3 text-2xl font-medium leading-[1.25] tracking-tight text-[#241A16] md:text-4xl"
                  style={{ fontFamily: 'Fraunces, Georgia, "Times New Roman", serif' }}
                >
                  Focused committees for focused outcomes.
                </h2>
              </div>
              <p className="max-w-md text-sm leading-relaxed text-[#241A16]/75 font-normal">
                Sector sub-committees help convert member inputs into state-specific policy recommendations,
                promotion opportunities and business interactions.
              </p>
            </div>

            <div className="grid border-t border-l border-[#241A16]/10 sm:grid-cols-2 lg:grid-cols-4">
              {data.sectors.map((sectorName, index) => {
                const Icon = iconForSector(sectorName);
                return (
                  <motion.div
                    key={sectorName}
                    className="group relative flex min-h-[160px] flex-col justify-between border-r border-b border-[#241A16]/10 bg-white/60 p-6 transition-all duration-300 hover:bg-[#DA2128]"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.04 }}
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#DA2128]/10 text-[#DA2128] transition-all duration-300 group-hover:bg-white/20 group-hover:text-white group-hover:scale-105">
                      <Icon className="text-xl" />
                    </div>

                    <p
                      className="mt-6 text-base font-medium tracking-tight text-[#241A16] transition-colors duration-300 group-hover:text-white"
                      style={{ fontFamily: 'Fraunces, Georgia, "Times New Roman", serif' }}
                    >
                      {sectorName}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-6 py-16 lg:px-12 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr]">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[#DB6400]">
              Services
            </p>
            <h2
              className="mt-3 text-2xl font-medium leading-[1.25] tracking-tight text-[#241A16] md:text-4xl"
              style={{ fontFamily: 'Fraunces, Georgia, "Times New Roman", serif' }}
            >
              A chamber desk for companies operating in the state.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-[#241A16]/75 font-normal">
              From investment confidence and policy submissions to delegations, certificates and MoU facilitation,
              the council supports member companies with practical industry services.
            </p>
          </motion.div>

          <div className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.label}
                  className="group flex items-start gap-4 border-b border-[#241A16]/10 pb-5"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.06 }}
                >
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#DA2128]/10 text-[#DA2128] transition-all duration-300 group-hover:bg-[#DA2128] group-hover:text-white group-hover:scale-105">
                    <Icon className="text-lg" />
                  </span>

                  <p
                    className="text-base font-medium leading-snug tracking-tight text-[#241A16]"
                    style={{ fontFamily: 'Fraunces, Georgia, "Times New Roman", serif' }}
                  >
                    {service.label}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

<section id="initiatives" className="relative overflow-hidden bg-[#FFFBF7] text-[#12100E]">
  {/* Ambient Mesh Glow */}
  <div className="pointer-events-none absolute -right-20 -top-20 h-96 w-96 rounded-full bg-[#F59C00]/10 blur-3xl" />
  <div className="pointer-events-none absolute -bottom-20 -left-20 h-96 w-96 rounded-full bg-[#DA2128]/5 blur-3xl" />

  <div className="relative mx-auto max-w-7xl px-6 py-16 lg:px-12 lg:py-20">
    <div className="mb-12 max-w-2xl">
      <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[#D98200]">
        Flagship Initiatives
      </p>
      <h2
        className="mt-3 text-2xl font-semibold leading-[1.25] tracking-tight text-[#12100E] md:text-4xl"
        style={{ fontFamily: 'Fraunces, Georgia, "Times New Roman", serif' }}
      >
        Where the council creates momentum.
      </h2>
    </div>

    <div className="divide-y divide-black/10 border-y border-black/10">
      {initiatives.map((initiative, index) => (
        <motion.div
          key={initiative}
          className="group grid gap-4 py-5 md:grid-cols-[70px_1fr_32px] md:items-center"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.04 }}
        >
          <p className="text-xs font-bold uppercase tracking-widest text-[#D98200]">
            {String(index + 1).padStart(2, '0')}
          </p>

          <p
            className="text-base font-semibold tracking-tight text-[#12100E]/85 transition-colors duration-300 group-hover:text-[#12100E]"
            style={{ fontFamily: 'Fraunces, Georgia, "Times New Roman", serif' }}
          >
            {initiative}
          </p>

          <div className="hidden h-8 w-8 items-center justify-center rounded-md border border-black/10 bg-black/5 text-[#DA2128] transition-all duration-300 group-hover:bg-[#DA2128] group-hover:border-[#DA2128] group-hover:text-white md:flex">
            <FaExchangeAlt className="text-xs" />
          </div>
        </motion.div>
      ))}
    </div>
  </div>
</section>

      <section className="relative bg-[#FFFFFF] py-16 lg:py-20">
          <div className="absolute left-0 top-0 h-full w-1/3 bg-[#DA2128]" />

          <div className="relative mx-auto grid max-w-7xl gap-8 px-6 lg:grid-cols-[0.72fr_1.28fr] lg:px-12">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="flex flex-col justify-center py-4 text-white"
            >
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-white/10 text-[#F59C00]">
                <FaAward className="text-xl" />
              </div>

              <h2
                className="text-2xl font-medium leading-[1.25] tracking-tight text-white md:text-4xl"
                style={{ fontFamily: 'Fraunces, Georgia, "Times New Roman", serif' }}
              >
                Strategic Priorities
              </h2>
              <p className="mt-3 text-sm font-normal text-white/80">
                Current direction for deeper state impact.
              </p>
            </motion.div>

            <div className="rounded-r-lg bg-[#FAF7F2] p-6 shadow-sm lg:p-10">
              {priorities.map((priority, index) => (
                <motion.div
                  key={priority}
                  className="flex items-baseline gap-4 border-b border-[#241A16]/10 py-5 first:pt-0 last:border-b-0 last:pb-0"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <span className="text-xs font-semibold uppercase tracking-widest text-[#DA2128]">
                    0{index + 1}
                  </span>

                  <p
                    className="text-lg font-medium tracking-tight text-[#241A16]"
                    style={{ fontFamily: 'Fraunces, Georgia, "Times New Roman", serif' }}
                  >
                    {priority}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
    </main>
  );
}